import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { ChevronLeft, ChevronRight, Xmark } from '@gravity-ui/icons';
import { grantAccess, listAccessGrants, revokeAccess } from '../../services/accessGrants';
import { getUsers } from '../../services/auth';
import { describeUserError } from '../../utils/apiError';
import { USER_FORMS, countRu } from '../../utils/pluralRu';

export const USERS_PAGE_SIZE = 100;


// GET /auth/users: { status, count, data: [...] }; count — размер страницы, не общее число.
const normalizeUsers = (response) => (Array.isArray(response?.data) ? response.data : []);

const userId = (user) => user?.user_id ?? user?.id;

const userLabel = (user) => {
  const fullName = [user?.name, user?.surname].filter(Boolean).join(' ');
  return fullName || user?.email || user?.username || '—';
};

const AccessGrantsModal = ({ entityType, entityId, entityName, hasNested = true, onClose }) => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [offset, setOffset] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [addWithNested, setAddWithNested] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState(null);

  const loadGrants = useCallback(async () => {
    try {
      const data = await listAccessGrants(entityType, entityId);
      setGrants(data?.users || []);
    } catch (err) {
      setError(describeUserError(err, 'Не удалось загрузить список доступа'));
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    setLoading(true);
    loadGrants();
  }, [loadGrants]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    let cancelled = false;
    setUsersLoading(true);
    getUsers({
      limit: USERS_PAGE_SIZE,
      offset,
      ...(search ? { email_like: search } : {}),
    })
      .then((response) => {
        if (cancelled) return;
        setUsers(normalizeUsers(response));
        setUsersError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setUsers([]);
        setUsersError(describeUserError(err, 'Не удалось загрузить пользователей'));
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, offset, search]);

  const grantedIds = new Set(grants.map((g) => g.user_id));

  const runMutation = async (action, successText, failText) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setNotice('');
    setError('');
    try {
      await action();
      setNotice(successText);
      await loadGrants();
    } catch (err) {
      setError(describeUserError(err, failText));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleAdd = (user) =>
    runMutation(
      () => grantAccess(entityType, entityId, [userId(user)], hasNested && addWithNested),
      'Доступ выдан',
      'Не удалось выдать доступ',
    );

  const handleRevoke = async (user, withNested) => {
    await runMutation(
      () => revokeAccess(entityType, entityId, user.user_id, withNested),
      'Доступ отозван',
      'Не удалось отозвать доступ',
    );
    setRevokeTarget(null);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setOffset(0);
    setSearch(searchInput.trim());
  };

  return (
    <Modal show onHide={onClose} size="lg" className="admin-modal" aria-label="Доступ">
      <div className="modal-header">
        <h5 className="modal-title">Доступ · {entityName}</h5>
        <button type="button" className="btn-close" onClick={onClose} title="Закрыть" aria-label="Закрыть" />
      </div>
      <div className="modal-body">
        {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
        {notice && <div className="alert alert-success py-2" role="status">{notice}</div>}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="small text-muted">
            {loading ? 'Загрузка…' : (grants.length > 0 ? `Всего ${countRu(grants.length, USER_FORMS)}` : '')}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setPickerOpen((v) => !v)}
          >
            {pickerOpen ? 'Скрыть выбор' : 'Добавить пользователя'}
          </button>
        </div>

        {!loading && grants.length === 0 && !error && (
          <div className="text-muted mb-3">Пока ни у кого нет прямого доступа</div>
        )}

        <ul className="list-group mb-3">
          {grants.map((grant) => (
            <li key={grant.user_id} className="list-group-item">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div>{userLabel(grant)}</div>
                  {grant.email && userLabel(grant) !== grant.email && <div className="small text-muted">{grant.email}</div>}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  disabled={busy}
                  aria-label={`Отозвать доступ ${userLabel(grant)}`}
                  onClick={() => setRevokeTarget(revokeTarget === grant.user_id ? null : grant.user_id)}
                >
                  Отозвать
                </button>
              </div>
              {revokeTarget === grant.user_id && (
                <div className="mt-2 border-top pt-2">
                  {hasNested ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary me-2"
                        disabled={busy}
                        onClick={() => handleRevoke(grant, false)}
                      >
                        Только здесь
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger me-2"
                        disabled={busy}
                        onClick={() => handleRevoke(grant, true)}
                      >
                        Вместе с вложенными
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger me-2"
                      disabled={busy}
                      onClick={() => handleRevoke(grant, false)}
                    >
                      Отозвать доступ
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-link"
                    onClick={() => setRevokeTarget(null)}
                  >
                    Отмена
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>

        {pickerOpen && (
          <div className="border rounded p-3">
            <form className="d-flex gap-2 mb-2" onSubmit={handleSearchSubmit}>
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Поиск по почте"
                aria-label="Поиск пользователя по почте"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button type="submit" className="btn btn-sm btn-outline-secondary">Найти</button>
            </form>
            {hasNested && (
              <div className="mb-2">
                <div className="form-check form-check-inline">
                  <input
                    id="access-add-here"
                    type="radio"
                    name="access-add-scope"
                    className="form-check-input"
                    checked={!addWithNested}
                    onChange={() => setAddWithNested(false)}
                  />
                  <label className="form-check-label" htmlFor="access-add-here">Только здесь</label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    id="access-add-nested"
                    type="radio"
                    name="access-add-scope"
                    className="form-check-input"
                    checked={addWithNested}
                    onChange={() => setAddWithNested(true)}
                  />
                  <label className="form-check-label" htmlFor="access-add-nested">Вместе с вложенными</label>
                </div>
              </div>
            )}
            {usersError && <div className="text-danger small mb-2">{usersError}</div>}
            {usersLoading && <div className="small text-muted">Загрузка…</div>}
            {!usersLoading && !usersError && users.length === 0 && (
              <div className="text-muted small">Пользователи не найдены</div>
            )}
            <ul className="list-group mb-2">
              {users.map((user) => {
                const id = userId(user);
                const already = grantedIds.has(id);
                return (
                  <li key={id} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <div>{userLabel(user)}</div>
                      {user.email && userLabel(user) !== user.email && <div className="small text-muted">{user.email}</div>}
                    </div>
                    {already ? (
                      <span className="small text-muted">Уже есть доступ</span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={busy}
                        onClick={() => handleAdd(user)}
                        aria-label={`Выдать доступ ${userLabel(user)}`}
                      >
                        Выдать доступ
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="d-flex justify-content-between align-items-center">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={usersLoading || offset === 0}
                onClick={() => setOffset(Math.max(0, offset - USERS_PAGE_SIZE))}
              >
                <ChevronLeft width={16} height={16} aria-hidden="true" /> Назад
              </button>
              <span className="small text-muted">{`Страница ${Math.floor(offset / USERS_PAGE_SIZE) + 1}`}</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={usersLoading || users.length < USERS_PAGE_SIZE}
                onClick={() => setOffset(offset + USERS_PAGE_SIZE)}
              >
                Далее <ChevronRight width={16} height={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          <Xmark width={16} height={16} aria-hidden="true" /> Закрыть
        </button>
      </div>
    </Modal>
  );
};

export default AccessGrantsModal;
